import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, IconButton, Typography, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../../context/PermissionContext';
import { API_HEADERS } from '../../../../constants/apiHeaders';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import api from '../../../../services/httpClient';
import { VACANCY_ENDPOINTS } from '../../../../services/endpoints/rec/vacancy.endpoints';
import { vacancyService } from '../../../../services/vacancy.service';
import type {
  JiraStatus,
  VacancyAnswerType,
  VacancyStage,
  VacancyStageQuestion,
  VacancyStageType,
} from '../../../../services/types/vacancy.types';
import Button from '../../../common/Button';
import ConfirmationModal from '../../../common/ConfirmationModal';
import Loading from '../../../common/Loading';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';
import PredefinedQuestionsModal from './PredefinedQuestionsModal';
import EditIcon from '../../../common/icons/EditIcon';
import CloseIcon from '../../../common/icons/CloseIcon';
import { ReactComponent as AddRegularQuestionIcon } from '../../../../assets/icons/Add Regular Question Button.svg';
import { ReactComponent as OpenPredefinedQuestionIcon } from '../../../../assets/icons/Open Predefined Question Button.svg';
import {
  levelQuestionsEqual,
  validateQuestionRowsForLevel,
} from '../../../../utils/vacancyValidation';

interface QuestionRow {
  _clientId: string;
  id: number | null;
  interviewQuestionId: number | null;
  question: string;
  answerType: VacancyAnswerType | '';
  /** GENERAL stage only: the applicant column the answer is written to (read-only). */
  targetColumn: string | null;
  applicantResponse: number;
  sortOrder: number;
  isActive: boolean;
  _source: 'normal' | 'predefined';
  _isExisting: boolean; // true if loaded from backend (has db id), false if newly added in UI
  _isEditing?: boolean; // true if this specific row is in edit mode
}

/** A vacancy stage with its questions. `_clientId` is stable before and after the stage is saved. */
interface StageRow {
  _clientId: string;
  id: number | null;
  stageType: VacancyStageType;
  name: string;
  jiraStatusName: string;
  sortOrder: number;
  allocatedApplicantCount: number;
  /** Kept so a stage update does not clear values this screen does not edit. */
  completionMessage: string | null;
  sendCompletionMessage: boolean;
  _isExisting: boolean;
  /** The name / Jira status fields are editable. */
  _isEditing: boolean;
  saved: { name: string; jiraStatusName: string };
  questions: QuestionRow[];
  /** Last version that matches the database (used by Discard). */
  savedQuestions: QuestionRow[];
}

interface StageFieldErrors {
  name?: string;
  jiraStatusName?: string;
}

interface QuestionsErrorState {
  pageError: string | null;
  saveError: string | null;
  fieldErrors: Record<string, { question?: string; answerType?: string }>;
}

interface VacancyQuestionsFormProps {
  type: 'add' | 'edit';
  addedVacancyId?: number | null;
  onCancel: () => void;
  onSuccess: (message?: string) => void;
  onError: (message?: string) => void;
}

const ANSWER_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Boolean' },
];

const STAGE_NAME_MAX_LENGTH = 100;
// Stage names are always typed by the user; nothing is pre-filled. These are only headings shown
// while a stage has no name yet.
const GENERAL_SECTION_TITLE = 'General Questions';
const SECTION_WIDTH = '1200px';

const bySortOrder = <T extends { sortOrder: number }>(a: T, b: T) => a.sortOrder - b.sortOrder;

/** GENERAL stage first, then the interview stages in pipeline order. */
const sortStages = (stages: StageRow[]): StageRow[] =>
  [...stages].sort((a, b) => {
    if (a.stageType !== b.stageType) return a.stageType === 'GENERAL' ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });

const toQuestionRow = (q: VacancyStageQuestion): QuestionRow => ({
  _clientId: `${q.id}-${Date.now()}-${Math.random()}`,
  id: q.id,
  interviewQuestionId: q.interviewQuestionId,
  question: q.question,
  answerType: q.answerType,
  targetColumn: q.targetColumn,
  applicantResponse: q.applicantResponse,
  sortOrder: q.sortOrder,
  isActive: true,
  _source: q.interviewQuestionId ? 'predefined' : 'normal',
  _isExisting: true,
});

const toStageRow = (
  stage: VacancyStage,
  questions: QuestionRow[],
  clientId?: string,
): StageRow => ({
  _clientId: clientId ?? `stage-${stage.id}-${Math.random()}`,
  id: stage.id,
  stageType: stage.stageType,
  name: stage.name,
  jiraStatusName: stage.jiraStatusName,
  sortOrder: stage.sortOrder,
  allocatedApplicantCount: stage.allocatedApplicantCount,
  completionMessage: stage.completionMessage,
  sendCompletionMessage: stage.sendCompletionMessage,
  _isExisting: true,
  _isEditing: false,
  saved: { name: stage.name, jiraStatusName: stage.jiraStatusName },
  questions,
  savedQuestions: JSON.parse(JSON.stringify(questions)),
});

const apiMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message || fallback;

const VacancyQuestionsForm: React.FC<VacancyQuestionsFormProps> = ({
  type,
  addedVacancyId,
  onCancel,
  onSuccess,
  onError,
}) => {
  const params = useParams();
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const [stages, setStages] = useState<StageRow[]>([]);
  const [jiraStatuses, setJiraStatuses] = useState<JiraStatus[]>([]);
  const [stageErrors, setStageErrors] = useState<Record<string, StageFieldErrors>>({});
  const [savingStageId, setSavingStageId] = useState<string | null>(null);
  const [savingQuestionsStageId, setSavingQuestionsStageId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [stageToDelete, setStageToDelete] = useState<StageRow | null>(null);
  const [isDeletingStage, setIsDeletingStage] = useState(false);
  const [errors, setErrors] = useState<QuestionsErrorState>({
    pageError: null,
    saveError: null,
    fieldErrors: {},
  });
  const [isPredefinedModalOpen, setIsPredefinedModalOpen] = useState(false);
  const [predefinedTargetStageId, setPredefinedTargetStageId] = useState<string | null>(null);

  const vacancyId = addedVacancyId || params?.id;
  const canEditQuestions =
    (type === 'add' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_ADD)) ||
    (type === 'edit' && hasPermission(PERMISSION_IDS.VACANCY_QUESTIONS_SAVE_EDIT));

  const generalStage = stages.find((s) => s.stageType === 'GENERAL');
  const interviewStages = stages.filter((s) => s.stageType === 'INTERVIEW');
  const hasUnsavedStage = stages.some((s) => s.id === null);

  // Collect IDs of predefined questions already added to this vacancy
  const usedPredefinedQuestionIds: number[] = stages.flatMap((stage) =>
    stage.questions
      .filter((q) => q._source === 'predefined' && q.interviewQuestionId != null)
      .map((q) => q.interviewQuestionId as number),
  );

  useEffect(() => {
    if ((type === 'add' && pageId !== 8) || (type === 'edit' && pageId !== 11)) {
      setPageId(type === 'add' ? 8 : 11);
      return;
    }

    if (!vacancyId) return;

    const fetchStages = async () => {
      setIsLoading(true);
      try {
        const [statusResponse, stageResponse] = await Promise.all([
          vacancyService.getJiraStatuses(),
          vacancyService.getVacancyStages(Number(vacancyId)),
        ]);
        setJiraStatuses(statusResponse.data.data || []);

        const loaded = await Promise.all(
          (stageResponse.data.data || []).map(async (stage) => {
            const questionResponse = await vacancyService.getVacancyStageQuestions(
              Number(vacancyId),
              stage.id,
            );
            const questions = (questionResponse.data.data || []).map(toQuestionRow).sort(bySortOrder);
            return toStageRow(stage, questions);
          }),
        );
        setStages(sortStages(loaded));
      } catch (error: any) {
        console.error('Error fetching vacancy stages:', error);
        onError('Failed to load questions. Please try again.');
        setErrors((prev) => ({ ...prev, pageError: 'Failed to load questions' }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, vacancyId, pageId, setPageId]);

  // ---------------------------------------------------------------------------
  // State helpers
  // ---------------------------------------------------------------------------

  const patchStage = (clientId: string, patch: Partial<StageRow>) => {
    setStages((prev) => prev.map((s) => (s._clientId === clientId ? { ...s, ...patch } : s)));
  };

  const patchStageQuestions = (
    clientId: string,
    change: (questions: QuestionRow[]) => QuestionRow[],
  ) => {
    setStages((prev) =>
      prev.map((s) => (s._clientId === clientId ? { ...s, questions: change(s.questions) } : s)),
    );
  };

  const clearStageError = (clientId: string, field: keyof StageFieldErrors) => {
    setStageErrors((prev) => ({
      ...prev,
      [clientId]: { ...prev[clientId], [field]: undefined },
    }));
  };

  // ---------------------------------------------------------------------------
  // Stage actions
  // ---------------------------------------------------------------------------

  /** Statuses offered for a stage type: the GENERAL stage only gets the General-eligible ones (e.g. OpenCV). */
  const allowedStatusesFor = (stageType: VacancyStageType): JiraStatus[] =>
    stageType === 'GENERAL' ? jiraStatuses.filter((s) => s.forGeneral) : jiraStatuses;

  const newStageRow = (stageType: VacancyStageType, name: string): StageRow => ({
    _clientId: `new-stage-${Date.now()}-${Math.random()}`,
    id: null,
    stageType,
    name,
    // When only one status is possible (General -> OpenCV) it is selected by default; otherwise the
    // user picks it. The name is never pre-filled.
    jiraStatusName: (() => {
      const options = allowedStatusesFor(stageType);
      return options.length === 1 ? options[0].name : '';
    })(),
    sortOrder: stages.length + 1,
    allocatedApplicantCount: 0,
    completionMessage: null,
    sendCompletionMessage: true,
    _isExisting: false,
    _isEditing: true,
    saved: { name: '', jiraStatusName: '' },
    questions: [],
    savedQuestions: [],
  });

  const addGeneralStage = () => {
    if (generalStage) return;
    setStages((prev) => sortStages([...prev, newStageRow('GENERAL', '')]));
  };

  const addInterviewStage = () => {
    // Interview stages need the saved general stage first.
    if (!generalStage?.id) return;
    setStages((prev) => sortStages([...prev, newStageRow('INTERVIEW', '')]));
  };

  const validateStage = (stage: StageRow): boolean => {
    const next: StageFieldErrors = {};
    const name = stage.name.trim();
    if (!name) {
      next.name = 'Stage name is required';
    } else if (name.length > STAGE_NAME_MAX_LENGTH) {
      next.name = `Stage name must be at most ${STAGE_NAME_MAX_LENGTH} characters`;
    }
    if (!stage.jiraStatusName) {
      next.jiraStatusName = 'Jira status is required';
    }
    setStageErrors((prev) => ({ ...prev, [stage._clientId]: next }));
    return Object.keys(next).length === 0;
  };

  const handleStageApiError = (stage: StageRow, error: any, fallback: string) => {
    const fieldErrors = error?.response?.data?.errors as Record<string, string> | undefined;
    if (fieldErrors && (fieldErrors.name || fieldErrors.jiraStatusName)) {
      setStageErrors((prev) => ({
        ...prev,
        [stage._clientId]: { name: fieldErrors.name, jiraStatusName: fieldErrors.jiraStatusName },
      }));
    }
    onError(apiMessage(error, fallback));
  };

  const handleSaveStage = async (stage: StageRow) => {
    if (!vacancyId) {
      onError('Vacancy ID is required.');
      return;
    }
    if (!validateStage(stage)) {
      onError('Please fix the errors before saving.');
      return;
    }

    setSavingStageId(stage._clientId);
    const payload = {
      stageType: stage.stageType,
      name: stage.name.trim(),
      jiraStatusName: stage.jiraStatusName,
      completionMessage: stage.completionMessage,
      sendCompletionMessage: stage.sendCompletionMessage,
    };

    try {
      if (stage.id === null) {
        const response = await vacancyService.createVacancyStage(Number(vacancyId), payload);
        const created = response.data.data;
        // Creating the GENERAL stage copies the master general questions into it.
        const questionResponse = await vacancyService.getVacancyStageQuestions(
          Number(vacancyId),
          created.id,
        );
        const questions = (questionResponse.data.data || []).map(toQuestionRow).sort(bySortOrder);
        setStages((prev) =>
          sortStages(
            prev.map((s) =>
              s._clientId === stage._clientId ? toStageRow(created, questions, s._clientId) : s,
            ),
          ),
        );
        onSuccess(
          stage.stageType === 'GENERAL'
            ? 'General questions added successfully!'
            : 'Stage added successfully!',
        );
      } else {
        const response = await vacancyService.updateVacancyStage(
          Number(vacancyId),
          stage.id,
          payload,
        );
        const updated = response.data.data;
        patchStage(stage._clientId, {
          name: updated.name,
          jiraStatusName: updated.jiraStatusName,
          sortOrder: updated.sortOrder,
          completionMessage: updated.completionMessage,
          sendCompletionMessage: updated.sendCompletionMessage,
          saved: { name: updated.name, jiraStatusName: updated.jiraStatusName },
          _isEditing: false,
        });
        onSuccess('Stage updated successfully!');
      }
    } catch (error: any) {
      console.error('Error saving stage:', error);
      handleStageApiError(stage, error, 'Failed to save stage. Please try again.');
    } finally {
      setSavingStageId(null);
    }
  };

  const handleDiscardStage = (stage: StageRow) => {
    if (stage.id === null) {
      setStages((prev) => prev.filter((s) => s._clientId !== stage._clientId));
    } else {
      patchStage(stage._clientId, {
        name: stage.saved.name,
        jiraStatusName: stage.saved.jiraStatusName,
        _isEditing: false,
      });
    }
    setStageErrors((prev) => {
      const { [stage._clientId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const requestDeleteStage = (stage: StageRow) => {
    if (stage.id === null) {
      handleDiscardStage(stage);
      return;
    }
    setStageToDelete(stage);
  };

  const confirmDeleteStage = async () => {
    const stage = stageToDelete;
    if (!stage || stage.id === null || !vacancyId) return;

    setIsDeletingStage(true);
    try {
      await vacancyService.deleteVacancyStage(Number(vacancyId), stage.id);
      setStages((prev) => prev.filter((s) => s._clientId !== stage._clientId));
      onSuccess('Stage deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting stage:', error);
      // 409: allocated to applicants, or the general stage still has interview stages.
      onError(apiMessage(error, 'Failed to delete stage. Please try again.'));
    } finally {
      setIsDeletingStage(false);
      setStageToDelete(null);
    }
  };

  const moveStage = async (stage: StageRow, direction: -1 | 1) => {
    if (!vacancyId || !generalStage?.id || isReordering) return;
    const index = interviewStages.findIndex((s) => s._clientId === stage._clientId);
    const target = index + direction;
    if (index === -1 || target < 0 || target >= interviewStages.length) return;

    const reordered = [...interviewStages];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const stageIds = [generalStage.id, ...reordered.map((s) => s.id as number)];

    setIsReordering(true);
    try {
      const response = await vacancyService.reorderVacancyStages(Number(vacancyId), stageIds);
      const orderById = new Map((response.data.data || []).map((s) => [s.id, s.sortOrder]));
      setStages((prev) =>
        sortStages(
          prev.map((s) =>
            s.id !== null && orderById.has(s.id) ? { ...s, sortOrder: orderById.get(s.id)! } : s,
          ),
        ),
      );
      onSuccess('Stage order updated successfully!');
    } catch (error: any) {
      console.error('Error reordering stages:', error);
      onError(apiMessage(error, 'Failed to reorder stages. Please try again.'));
    } finally {
      setIsReordering(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Question actions (per stage)
  // ---------------------------------------------------------------------------

  const clearRowErrors = (clientId: string) => {
    setErrors((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, [clientId]: {} },
    }));
  };

  const addNormalQuestion = (stage: StageRow) => {
    const newQuestion: QuestionRow = {
      _clientId: `${Date.now()}-${Math.random()}`,
      id: null,
      interviewQuestionId: null,
      question: '',
      answerType: '',
      targetColumn: null,
      applicantResponse: 0,
      sortOrder: stage.questions.length + 1,
      isActive: true,
      _source: 'normal',
      _isExisting: false, // Newly added, editable
    };
    patchStageQuestions(stage._clientId, (questions) => [...questions, newQuestion]);
    clearRowErrors(newQuestion._clientId);
  };

  const openPredefinedModal = (stage: StageRow) => {
    setPredefinedTargetStageId(stage._clientId);
    setIsPredefinedModalOpen(true);
  };

  const handlePredefinedQuestionSelect = (predefinedQuestion: {
    id: number;
    question: string;
    answerType: VacancyAnswerType;
  }) => {
    const target = stages.find((s) => s._clientId === predefinedTargetStageId);
    if (!target) return;

    const newQuestion: QuestionRow = {
      _clientId: `${Date.now()}-${Math.random()}`,
      id: null,
      interviewQuestionId: predefinedQuestion.id,
      question: predefinedQuestion.question,
      answerType: predefinedQuestion.answerType,
      targetColumn: null,
      applicantResponse: 0,
      sortOrder: target.questions.length + 1,
      isActive: true,
      _source: 'predefined',
      _isExisting: false,
    };
    // Several selected questions arrive one by one, so append against the latest state.
    patchStageQuestions(target._clientId, (questions) => [
      ...questions,
      { ...newQuestion, sortOrder: questions.length + 1 },
    ]);

    setIsPredefinedModalOpen(false);
    setPredefinedTargetStageId(null);
    clearRowErrors(newQuestion._clientId);
  };

  const updateQuestion = (clientId: string, field: keyof QuestionRow, value: any) => {
    setStages((prev) =>
      prev.map((stage) =>
        stage.questions.some((q) => q._clientId === clientId)
          ? {
              ...stage,
              questions: stage.questions.map((q) =>
                q._clientId === clientId ? { ...q, [field]: value } : q,
              ),
            }
          : stage,
      ),
    );

    // Clear field error when user edits
    setErrors((prev) => ({
      ...prev,
      fieldErrors: {
        ...prev.fieldErrors,
        [clientId]: {
          ...prev.fieldErrors[clientId],
          [field]: undefined,
        },
      },
    }));
  };

  const moveQuestion = (stage: StageRow, fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    patchStageQuestions(stage._clientId, (questions) => {
      const reordered = [...questions];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      // Recompute sortOrder
      return reordered.map((q, idx) => ({ ...q, sortOrder: idx + 1 }));
    });
  };

  const removeQuestionFromState = (clientId: string) => {
    const strip = (questions: QuestionRow[]) =>
      questions.filter((q) => q._clientId !== clientId).map((q, idx) => ({ ...q, sortOrder: idx + 1 }));
    setStages((prev) =>
      prev.map((stage) =>
        stage.questions.some((q) => q._clientId === clientId)
          ? { ...stage, questions: strip(stage.questions), savedQuestions: strip(stage.savedQuestions) }
          : stage,
      ),
    );
    setErrors((prev) => {
      const { [clientId]: _removed, ...rest } = prev.fieldErrors;
      return { ...prev, fieldErrors: rest };
    });
  };

  const deleteQuestion = async (clientId: string) => {
    const questionToDelete = stages
      .flatMap((stage) => stage.questions)
      .find((q) => q._clientId === clientId);
    if (!questionToDelete) return;

    // A question that was never saved only lives in the UI.
    if (!questionToDelete.id) {
      removeQuestionFromState(clientId);
      return;
    }

    // Saved questions are removed with their own DELETE (the stage save never deletes).
    try {
      await api.delete(VACANCY_ENDPOINTS.deleteQuestion(questionToDelete.id), {
        headers: { ...API_HEADERS.VACANCY_INTERVIEW_QUESTIONS_DELETE },
      });
      removeQuestionFromState(clientId);
      onSuccess('Question deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting question:', error);
      onError(apiMessage(error, 'Failed to delete question. Please try again.'));
    }
  };

  const validateQuestions = (stage: StageRow): boolean => {
    const { fieldErrors: newFieldErrors, hasErrors } = validateQuestionRowsForLevel(stage.questions);

    setErrors((prev) => ({
      ...prev,
      fieldErrors: { ...prev.fieldErrors, ...newFieldErrors },
      saveError: hasErrors ? 'Please fix the errors before saving.' : null,
    }));

    return !hasErrors;
  };

  const handleDiscardQuestions = (stage: StageRow) => {
    // Revert the stage questions back to the last saved state (closes edit modes and removes unsaved changes)
    patchStageQuestions(stage._clientId, () => JSON.parse(JSON.stringify(stage.savedQuestions)));

    setErrors((prev) => {
      const newFieldErrors = { ...prev.fieldErrors };
      stage.questions.forEach((q) => {
        delete newFieldErrors[q._clientId];
      });
      return { ...prev, fieldErrors: newFieldErrors, saveError: null };
    });
  };

  const handleSaveQuestions = async (stage: StageRow) => {
    if (!validateQuestions(stage)) {
      onError('Please fix the errors before saving.');
      return;
    }

    if (!vacancyId || stage.id === null) {
      onError('Save the stage before saving its questions.');
      return;
    }

    setSavingQuestionsStageId(stage._clientId);
    setErrors((prev) => ({ ...prev, saveError: null }));

    try {
      // Position in the list is the display order.
      const response = await vacancyService.saveVacancyStageQuestions(
        Number(vacancyId),
        stage.id,
        stage.questions.map((q) => ({
          id: q.id,
          interviewQuestionId: q.interviewQuestionId,
          question: q.question.trim(),
          answerType: q.answerType as VacancyAnswerType,
        })),
      );

      // The response carries the saved questions with their ids, so sync both draft and saved.
      const saved = (response.data.data || []).map(toQuestionRow).sort(bySortOrder);
      patchStage(stage._clientId, {
        questions: saved,
        savedQuestions: JSON.parse(JSON.stringify(saved)),
      });

      onSuccess('Questions saved successfully!');
    } catch (error: any) {
      console.error('Error saving questions:', error);
      const errorMessage = apiMessage(error, 'Failed to save questions. Please try again.');
      onError(errorMessage);
      setErrors((prev) => ({ ...prev, saveError: errorMessage }));
    } finally {
      setSavingQuestionsStageId(null);
    }
  };

  const hasQuestionChanges = (stage: StageRow): boolean =>
    !levelQuestionsEqual(stage.questions, stage.savedQuestions);

  const hasEditingQuestions = (stage: StageRow): boolean =>
    stage.questions.some((q) => q._isEditing === true);

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  const arrowButtonSx = {
    width: 28,
    height: 28,
    borderRadius: '50%',
    border: '1px solid #D0D5DD',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      fontSize: 16,
      color: '#667085',
    },
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
    '&.Mui-disabled': {
      backgroundColor: '#F2F4F7',
      borderColor: '#E4E7EC',
      '& .MuiSvgIcon-root': {
        color: '#D0D5DD',
      },
    },
  } as const;

  const addIconButtonSx = {
    p: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
      opacity: 0.8,
    },
    '& svg': {
      width: '100%',
      height: '100%',
      display: 'block',
    },
  } as const;

  /** Jira status options; keeps a saved status that is no longer active so the field never looks empty. */
  const statusOptionsFor = (stage: StageRow) => {
    const options = allowedStatusesFor(stage.stageType).map((s) => ({ value: s.name, label: s.name }));
    if (stage.jiraStatusName && !options.some((o) => o.value === stage.jiraStatusName)) {
      options.unshift({ value: stage.jiraStatusName, label: stage.jiraStatusName });
    }
    return options;
  };

  const renderStageDetails = (stage: StageRow) => {
    const isLocked = stage._isExisting && !stage._isEditing;
    const fieldErrors = stageErrors[stage._clientId] ?? {};
    const isSavingStage = savingStageId === stage._clientId;

    return (
      <Box className="edit-sub-section" sx={{ width: SECTION_WIDTH, mb: 1, position: 'relative' }}>
        {isLocked && canEditQuestions && (
          <IconButton
            className="view-edit-icon"
            onClick={() => patchStage(stage._clientId, { _isEditing: true })}
            sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}
          >
            <EditIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', gap: 7 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              label="Stage Name"
              placeholder="Enter stage name"
              value={stage.name}
              onChange={(e) => {
                patchStage(stage._clientId, { name: e.target.value });
                clearStageError(stage._clientId, 'name');
              }}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              required
              disabled={isLocked}
              inputProps={{ maxLength: STAGE_NAME_MAX_LENGTH }}
              sx={{ width: '100%' }}
            />
          </Box>
          <Box>
            <SelectField
              label="After"
              placeholder="Select Status"
              width="360px"
              options={statusOptionsFor(stage)}
              value={stage.jiraStatusName}
              onChange={(e) => {
                patchStage(stage._clientId, { jiraStatusName: String(e.target.value) });
                clearStageError(stage._clientId, 'jiraStatusName');
              }}
              disabled={isLocked}
              error={!!fieldErrors.jiraStatusName}
              helperText={fieldErrors.jiraStatusName}
              required
            />
          </Box>
        </Box>
        {!isLocked && canEditQuestions && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => handleDiscardStage(stage)}
              className="edit-section-cancel-button"
              disabled={isSavingStage}
              width="150px"
            >
              {stage.id === null ? 'Cancel' : 'Discard'}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSaveStage(stage)}
              isLoading={isSavingStage}
              className="edit-section-save-button"
              disabled={isSavingStage}
              width="150px"
            >
              {isSavingStage ? 'Saving...' : stage.id === null ? 'Add Stage' : 'Save'}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  const renderQuestionRows = (stage: StageRow) => {
    const questions = stage.questions;
    const isGeneral = stage.stageType === 'GENERAL';

    return (
      <>
        {questions.map((question, index) => (
          <Box
            key={question._clientId}
            className="edit-sub-section"
            sx={{ width: SECTION_WIDTH, mb: 1, position: 'relative' }}
          >
            {/* Delete Icon - general questions are fixed, only their wording can change */}
            {!isGeneral && (
              <IconButton
                onClick={() => deleteQuestion(question._clientId)}
                sx={{
                  position: 'absolute',
                  top: '13px',
                  right: question._isExisting && !question._isEditing ? '40px' : '8px',
                  zIndex: 1,
                  padding: '2px',
                  '& svg': {
                    fontSize: 20,
                  },
                  '& svg path': {
                    fill: '#E60e0E',
                  },
                  '& svg circle': {
                    fill: '#E9584526',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            )}

            {/* Edit Icon - only for existing questions that are not in edit mode */}
            {question._isExisting && !question._isEditing && (
              <IconButton
                className="view-edit-icon"
                onClick={() => updateQuestion(question._clientId, '_isEditing', true)}
                sx={{ position: 'absolute', top: '8px', right: '8px', zIndex: 1 }}
              >
                <EditIcon />
              </IconButton>
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center', mt: 1 }}>
                <IconButton
                  size="small"
                  disabled={index === 0 || (question._isExisting && !question._isEditing)}
                  onClick={() => moveQuestion(stage, index, index - 1)}
                  sx={arrowButtonSx}
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === questions.length - 1 || (question._isExisting && !question._isEditing)}
                  onClick={() => moveQuestion(stage, index, index + 1)}
                  sx={arrowButtonSx}
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', gap: 7 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label={
                      isGeneral
                        ? 'Question (G)'
                        : question._source === 'predefined'
                          ? 'Question (P)'
                          : 'Question (N)'
                    }
                    multiline
                    rows={3}
                    value={question.question}
                    onChange={(e) => updateQuestion(question._clientId, 'question', e.target.value)}
                    error={!!errors.fieldErrors[question._clientId]?.question}
                    helperText={
                      errors.fieldErrors[question._clientId]?.question ||
                      (isGeneral && question.targetColumn
                        ? `The answer is saved to the applicant's ${question.targetColumn}`
                        : undefined)
                    }
                    required
                    disabled={question._isExisting && !question._isEditing}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        height: 'auto',
                        alignItems: 'flex-start',
                        padding: 0,
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                      '& .MuiOutlinedInput-inputMultiline': {
                        padding: '8px 12px',
                      },
                      '& .MuiFormHelperText-root': {
                        marginTop: '2px',
                        marginBottom: 0,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <SelectField
                    label="Answer Type"
                    width="360px"
                    options={ANSWER_TYPE_OPTIONS}
                    value={question.answerType}
                    onChange={(e) =>
                      updateQuestion(question._clientId, 'answerType', e.target.value)
                    }
                    disabled={
                      isGeneral ||
                      question._source === 'predefined' ||
                      (question._isExisting && !question._isEditing)
                    }
                    error={!!errors.fieldErrors[question._clientId]?.answerType}
                    helperText={errors.fieldErrors[question._clientId]?.answerType}
                    required={question._source === 'normal'}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ))}

        {questions.length === 0 && (
          <Box sx={{ py: 2, color: '#667085', fontSize: '14px' }}>No questions added yet.</Box>
        )}

        {/* Per-stage Save Button - aligned so Save button right edge matches 'Add predefined questions' icon */}
        {canEditQuestions && questions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, mb: 2, width: SECTION_WIDTH }}>
            <Button
              variant="outlined"
              onClick={() => handleDiscardQuestions(stage)}
              className="edit-section-cancel-button"
              disabled={
                savingQuestionsStageId !== null ||
                (!hasQuestionChanges(stage) && !hasEditingQuestions(stage))
              }
              width="150px"
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSaveQuestions(stage)}
              isLoading={savingQuestionsStageId === stage._clientId}
              className="edit-section-save-button"
              disabled={savingQuestionsStageId !== null || !hasQuestionChanges(stage)}
              width="150px"
            >
              {savingQuestionsStageId === stage._clientId ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        )}
      </>
    );
  };

  /** One stage: its name / Jira status and (once the stage is saved) its questions. */
  const renderStage = (stage: StageRow) => {
    const isGeneral = stage.stageType === 'GENERAL';
    const interviewIndex = interviewStages.findIndex((s) => s._clientId === stage._clientId);
    const canReorder = !isGeneral && stage.id !== null && !hasUnsavedStage && !isReordering;

    return (
      <Box key={stage._clientId} className="edit-section" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 0.5,
            width: SECTION_WIDTH,
          }}
        >
          <Typography className="edit-section-title">
            {stage.name || (isGeneral ? GENERAL_SECTION_TITLE : 'New Stage')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {!isGeneral && canEditQuestions && (
              <>
                <Tooltip title="Move stage up">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canReorder || interviewIndex <= 0}
                      onClick={() => moveStage(stage, -1)}
                      sx={arrowButtonSx}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move stage down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canReorder || interviewIndex === interviewStages.length - 1}
                      onClick={() => moveStage(stage, 1)}
                      sx={arrowButtonSx}
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {stage.id !== null && !isGeneral && canEditQuestions && (
              <>
                <Tooltip title="Add normal questions">
                  <IconButton onClick={() => addNormalQuestion(stage)} sx={addIconButtonSx}>
                    <AddRegularQuestionIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Add predefined questions">
                  <IconButton onClick={() => openPredefinedModal(stage)} sx={addIconButtonSx}>
                    <OpenPredefinedQuestionIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {canEditQuestions && (
              <Tooltip title={isGeneral ? 'Delete general questions' : 'Delete stage'}>
                <IconButton
                  onClick={() => requestDeleteStage(stage)}
                  sx={{
                    padding: '2px',
                    '& svg': { fontSize: 20 },
                    '& svg path': { fill: '#E60e0E' },
                    '& svg circle': { fill: '#E9584526' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {renderStageDetails(stage)}

        {stage.id === null ? (
          <Box sx={{ py: 1, color: '#667085', fontSize: '14px' }}>
            {isGeneral
              ? 'Add the general questions to load the questions candidates answer first.'
              : 'Save the stage to add its questions.'}
          </Box>
        ) : (
          renderQuestionRows(stage)
        )}
      </Box>
    );
  };

  return (
    <Box gap={3} display="flex" flexDirection="column">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {errors.pageError && (
            <Box sx={{ color: 'error.main', mb: 2 }}>{errors.pageError}</Box>
          )}
          {errors.saveError && (
            <Box sx={{ color: 'error.main', mb: 2 }}>{errors.saveError}</Box>
          )}

          {/* General questions: one stage per vacancy, required before any interview stage */}
          <Box className="edit-section" sx={{ mb: 0.5 }}>
            {!generalStage && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 0.5,
                  width: SECTION_WIDTH,
                }}
              >
                <Typography className="edit-section-title">General Questions</Typography>
                {canEditQuestions && (
                  <Button variant="contained" onClick={addGeneralStage} width="100px">
                    Add
                  </Button>
                )}
              </Box>
            )}
            {!generalStage && (
              <Box sx={{ py: 2, color: '#667085', fontSize: '14px' }}>
                Add the general questions first. Candidates answer them before any interview stage,
                and interview stages cannot be added without them.
              </Box>
            )}
          </Box>
          {generalStage && renderStage(generalStage)}

          {/* Interview stages: named, mapped to a Jira status, in pipeline order */}
          <Box className="edit-section" sx={{ mb: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 0.5,
                width: SECTION_WIDTH,
              }}
            >
              <Typography className="edit-section-title">Interview Stages</Typography>
              {canEditQuestions && (
                <Tooltip
                  title={
                    generalStage?.id
                      ? ''
                      : 'Add and save the general questions before adding interview stages'
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={addInterviewStage}
                      disabled={!generalStage?.id}
                      width="130px"
                    >
                      Add Stage
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            {interviewStages.length === 0 && (
              <Box sx={{ py: 2, color: '#667085', fontSize: '14px' }}>
                No interview stages added yet.
              </Box>
            )}
          </Box>
          {interviewStages.map((stage) => renderStage(stage))}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button variant="outlined" onClick={onCancel} className="edit-section-cancel-button">
              Back
            </Button>
          </Box>
        </>
      )}

      <PredefinedQuestionsModal
        open={isPredefinedModalOpen}
        onClose={() => {
          setIsPredefinedModalOpen(false);
          setPredefinedTargetStageId(null);
        }}
        onSelect={handlePredefinedQuestionSelect}
        usedQuestionIds={usedPredefinedQuestionIds}
      />

      <ConfirmationModal
        open={stageToDelete !== null}
        onClose={() => setStageToDelete(null)}
        onConfirm={confirmDeleteStage}
        title={stageToDelete?.stageType === 'GENERAL' ? 'Delete General Questions' : 'Delete Stage'}
        message={
          stageToDelete
            ? `Are you sure you want to delete <strong>${stageToDelete.name}</strong>? Its questions will be deleted too.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        isActioning={isDeletingStage}
      />
    </Box>
  );
};

export default VacancyQuestionsForm;
